import { coreLibraryInitialised } from "@glamcorner/core-cf-lib/lib/common/gcsession/utils";
import { AttributeFilter, CollectionDesignation, CollectionFilter } from "../../common/interfaces";
import { AvailabilityFilter } from "./containers/AvailabilityFilterContainer";
import { CatalogueEvents } from "@glamcorner/gc-types/generated/ipcBusEvents";
import { Style, StyleType } from "@glamcorner/gc-types/generated/types";
import { todayInSydney } from "../../common/clock";
import { createURLHashFromFilters } from "../../common/filterQueryUtils";
import { parseQueryString } from "@glamcorner/core-cf-lib/lib/common/utils";
import { getDaysCount, getRenderMode, RenderMode } from "./util";
import { Bus } from "../services/Bus";

const CATALOGUE_EVENTS = {
  FILTER_SELECTION: "filter-selection",
};

export const broadcastUsedLegacyFilterEvent = (filterString: string) => {
  Bus.broadcast(CatalogueEvents.UsedLegacyFilterUrl, {
    filterString,
    referrer: document.referrer,
    href: location.href,
    cookie: document.cookie,
  });
};

export class Analytics {
  private readonly isEnabled: boolean;

  constructor() {
    this.isEnabled = coreLibraryInitialised();
  }

  broadcastFilterEvent({
    attributeFilters,
    collectionFilter,
    availabilityFilter,
  }: {
    attributeFilters: AttributeFilter[];
    collectionFilter: CollectionFilter;
    availabilityFilter: AvailabilityFilter;
  }) {
    if (this.isEnabled) {
      const analyticsData: { [label: string]: string[] } = {};
      analyticsData["Designation"] = [collectionFilter.collectionDesignation];
      if (availabilityFilter.subAvailabilityFilter) {
        analyticsData["Available Now"] = [availabilityFilter.subAvailabilityFilter];
      }
      attributeFilters.forEach((filter) => {
        analyticsData[filter.label] = filter.options.map((option) => option.label);
      });

      Bus.broadcast(CATALOGUE_EVENTS.FILTER_SELECTION, analyticsData);
    }
  }
}

export const getStyleItemAnalyticsData = (
  style: Style,
  collectionDesignation: CollectionDesignation,
  isClearance: boolean,
  hashUrl?: string,
  position?: number
) => {
  const { record_type, discount_price_in_cents, price_in_cents } = style;

  const shouldRenderAsSubscription = getRenderMode(style, collectionDesignation) === RenderMode.SUBSCRIPTION;

  const { startDate, endDate, size } = hashUrl
    ? parseQueryString(hashUrl)
    : {
        startDate: undefined,
        endDate: undefined,
        size: undefined,
      };
  let price = 0;
  let variant = "";
  if (shouldRenderAsSubscription) {
    variant = "subscription";
  } else {
    if (isClearance) {
      variant = "clearance";
    } else if (record_type === StyleType.SaleProduct) {
      variant = "purchase";
    } else {
      variant = "on-demand";
    }
    if (discount_price_in_cents) {
      price = discount_price_in_cents / 100;
    } else if (price_in_cents) {
      price = price_in_cents / 100;
    }
  }

  const rentalDatesRange = getDaysCount(startDate as string, endDate as string);
  let rentalDaysInAdvance = getDaysCount(todayInSydney().format("YYYY-MM-DD"), startDate as string);

  if (typeof rentalDaysInAdvance === "number") {
    rentalDaysInAdvance = rentalDaysInAdvance - 1;
  }

  return {
    name: style.name,
    id: style.style_code,
    price: price.toString(),
    brand: style.designer,
    category: "",
    variant,
    size,
    rentalDatesRange,
    rentalDaysInAdvance,
    list: "catalogue",
    position,
  };
};

interface FilterAnalyticsData {
  value: string;
  type: string;
}
const getFilters = (
  collectionFilter: CollectionFilter,
  availabilityFilter: AvailabilityFilter,
  attributeFilters: AttributeFilter[]
) => {
  const filters: FilterAnalyticsData[] = [];
  Object.keys(collectionFilter).forEach((key) => {
    if (collectionFilter[key]) {
      filters.push({
        value: collectionFilter[key],
        type: key,
      });
    }
  });

  Object.keys(availabilityFilter).forEach((key) => {
    if (key === "deliveryDates") {
      if (
        availabilityFilter[key].endDate &&
        availabilityFilter[key].startDate &&
        collectionFilter.collectionDesignation !== CollectionDesignation.Subscription
      ) {
        filters.push({
          value: availabilityFilter[key].endDate,
          type: "endDate",
        });
        filters.push({
          value: availabilityFilter[key].startDate,
          type: "startDate",
        });
      }
    } else if (key === "subscriptionDates") {
      if (
        availabilityFilter[key].endDate &&
        availabilityFilter[key].startDate &&
        collectionFilter.collectionDesignation === CollectionDesignation.Subscription
      ) {
        filters.push({
          value: availabilityFilter[key].endDate,
          type: "endDate",
        });
        filters.push({
          value: availabilityFilter[key].startDate,
          type: "startDate",
        });
      }
    } else if (availabilityFilter[key]) {
      filters.push({
        value: availabilityFilter[key],
        type: key,
      });
    }
  });
  attributeFilters.forEach((attributeFilter) => {
    attributeFilter.options.forEach((option) => {
      filters.push({
        value: option.label,
        type: attributeFilter.label,
      });
    });
  });
  return filters;
};

export const getStyleItemsAnalyticsData = (
  styles: Style[],
  isClearance: boolean,
  collectionFilter: CollectionFilter,
  availabilityFilter: AvailabilityFilter,
  attributeFilters: AttributeFilter[]
) => {
  const hashUrl = createURLHashFromFilters(collectionFilter, availabilityFilter, attributeFilters);
  const products = styles.map((style, position) => {
    return getStyleItemAnalyticsData(style, collectionFilter.collectionDesignation, isClearance, hashUrl, position);
  });
  const filters = getFilters(collectionFilter, availabilityFilter, attributeFilters);
  return {
    products,
    filters,
  };
};
